import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdNotificationsNone, MdOpenInNew, MdSummarize } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { OutlineButton } from '../../../components/ui';
import { getAppUserActivitySummary } from '../../../repositories/appUser.repository';
import { formatDateTimeDisplay } from '../../../utils/personName';

function statusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('releas')) return 'green';
  if (s.includes('declin') || s.includes('cancel')) return 'red';
  if (s.includes('assign') || s.includes('select')) return 'blue';
  if (s.includes('recommend') || s.includes('active')) return 'yellow';
  return 'gray';
}

function prettyStatus(status) {
  if (!status) return '';
  const s = String(status);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function Stat({ label, value }) {
  return (
    <Box
      p={3}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      bg="bg.input"
      textAlign="center"
    >
      <Text fontSize="22px" fontWeight="800" lineHeight="1.1">
        {value ?? 0}
      </Text>
      <Text fontSize="12px" color="text.secondary" fontWeight="600" mt={1}>
        {label}
      </Text>
    </Box>
  );
}

function Section({ title, hint, children }) {
  return (
    <Box mb={6}>
      <Text fontSize="15px" fontWeight="800" mb={0.5}>
        {title}
      </Text>
      {hint ? (
        <Text fontSize="12px" color="text.secondary" mb={3}>
          {hint}
        </Text>
      ) : (
        <Box mb={3} />
      )}
      {children}
    </Box>
  );
}

function Empty({ label }) {
  return (
    <Text fontSize="13px" color="text.secondary" py={2}>
      {label}
    </Text>
  );
}

function SimpleRow({ what, where, when, status, onOpen }) {
  return (
    <Flex
      py={3}
      borderBottomWidth="1px"
      borderColor="border.subtle"
      gap={3}
      align="flex-start"
      justify="space-between"
      _last={{ borderBottomWidth: 0 }}
    >
      <Box minW={0} flex="1">
        <Flex align="center" gap={2} wrap="wrap" mb={0.5}>
          <Text fontSize="14px" fontWeight="700">
            {what}
          </Text>
          {status ? (
            <Badge colorScheme={statusColor(status)} fontSize="10px">
              {prettyStatus(status)}
            </Badge>
          ) : null}
        </Flex>
        {where ? (
          <Text fontSize="13px" color="text.secondary">
            {where}
          </Text>
        ) : null}
        {when ? (
          <Text fontSize="12px" color="text.secondary" mt={1}>
            {formatDateTimeDisplay(when)}
          </Text>
        ) : null}
      </Box>
      {onOpen ? (
        <OutlineButton
          size="sm"
          leftIcon={<MdOpenInNew />}
          onClick={onOpen}
          flexShrink={0}
        >
          Open
        </OutlineButton>
      ) : null}
    </Flex>
  );
}

function buildHistory(data) {
  const rows = [];

  for (const r of data.referrals || []) {
    const place = r.location && r.location !== '—' ? r.location : '';
    if (r.role === 'referrer') {
      rows.push({
        id: r.id,
        when: r.at,
        what: `Referred ${r.targetName || r.assetLabel || 'someone'}`,
        where: [r.requestNo, r.title, place].filter(Boolean).join(' · '),
        status: r.status,
        requestId: r.requestId,
      });
    } else {
      rows.push({
        id: r.id,
        when: r.at,
        what: r.referredByName
          ? `Recommended via ${r.referredByName}`
          : 'Was recommended for work',
        where: [r.requestNo, r.title, place].filter(Boolean).join(' · '),
        status: r.status,
        requestId: r.requestId,
      });
    }
  }

  for (const w of data.work || []) {
    const place = w.location && w.location !== '—' ? w.location : '';
    const who = w.assetLabel || w.title || 'Work';
    rows.push({
      id: w.id,
      when: w.at,
      what: w.status === 'Released' ? `Finished: ${who}` : `Working: ${who}`,
      where: [w.requestNo, place, w.requesterName !== '—' ? w.requesterName : '']
        .filter(Boolean)
        .join(' · '),
      status: w.status,
      requestId: w.requestId,
      userEquipmentId: w.userEquipmentId,
      appUserId: w.appUserId,
      rating: w.releaseRating,
    });
  }

  for (const req of data.agency?.requests || []) {
    rows.push({
      id: `req-${req.id}`,
      when: req.createdAt,
      what: `Raised ${req.requestNo}`,
      where: req.summary || (req.locations || []).join(', ') || 'Work request',
      status: req.status,
      requestId: req.id,
    });
  }

  rows.sort((a, b) => {
    const ta = a.when ? new Date(a.when).getTime() : 0;
    const tb = b.when ? new Date(b.when).getTime() : 0;
    return tb - ta;
  });

  return rows.slice(0, 40);
}

export function MemberActivitySummaryModal({
  isOpen,
  onClose,
  userId,
  userName,
  onNotify,
  /** Optional Hub overrides — keep navigation inside Hub instead of Classic routes. */
  onOpenIndent,
  onOpenEquipment,
  onOpenMember,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isOpen || !userId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const summary = await getAppUserActivitySummary(userId);
        if (!cancelled) setData(summary);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err?.message || 'Failed to load summary');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  const history = useMemo(() => (data ? buildHistory(data) : []), [data]);
  const overview = data?.overview || {};

  const openIndent = (requestId) => {
    if (!requestId) return;
    onClose?.();
    if (onOpenIndent) {
      onOpenIndent(requestId);
      return;
    }
    navigate(`/admin/equipments/indents/${requestId}`);
  };

  const openFleet = (ueId) => {
    if (!ueId) return;
    onClose?.();
    if (onOpenEquipment) {
      onOpenEquipment(ueId);
      return;
    }
    navigate(`/admin/equipments/fleet/${ueId}`);
  };

  const openMember = (id) => {
    if (!id) return;
    onClose?.();
    if (onOpenMember) {
      onOpenMember(id);
      return;
    }
    navigate(`/admin/users/${id}`);
  };

  const handleNotify = () => {
    onClose?.();
    window.setTimeout(() => onNotify?.(), 150);
  };

  const openHistoryRow = (row) => {
    if (row.userEquipmentId) openFleet(row.userEquipmentId);
    else if (row.appUserId) openMember(row.appUserId);
    else openIndent(row.requestId);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pr={12} pb={2}>
          <Flex align="center" justify="space-between" gap={3} wrap="wrap">
            <Flex align="center" gap={2} minW={0}>
              <Box as={MdSummarize} boxSize="22px" color="accent.amber" flexShrink={0} />
              <Box minW={0}>
                <Text fontSize="18px" fontWeight="800">
                  Quick summary
                </Text>
                <Text fontSize="13px" color="text.secondary" fontWeight="500" noOfLines={1}>
                  {data?.memberName || userName || `Member #${userId}`}
                </Text>
              </Box>
            </Flex>
            {onNotify ? (
              <OutlineButton
                size="sm"
                leftIcon={<MdNotificationsNone />}
                onClick={handleNotify}
              >
                Notify
              </OutlineButton>
            ) : null}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={6}>
          {loading ? (
            <Flex justify="center" py={16}>
              <Spinner color="accent.amber" />
            </Flex>
          ) : error ? (
            <Text color="red.400" py={8} textAlign="center">
              {error}
            </Text>
          ) : !data ? (
            <Empty label="Nothing to show" />
          ) : (
            <Box>
              <Section title="At a glance" hint="Only the numbers that matter.">
                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
                  {data.isAgency ? (
                    <Stat label="Requests raised" value={overview.requestsRaised} />
                  ) : null}
                  <Stat label="Times referred" value={overview.referralsAsReferrer} />
                  <Stat label="Times recommended" value={overview.referralsAsTarget} />
                  <Stat label="Jobs assigned" value={overview.assigned} />
                  <Stat label="Jobs finished" value={overview.released} />
                  {overview.declined > 0 ? (
                    <Stat label="Declined" value={overview.declined} />
                  ) : null}
                  {overview.avgReleaseRating != null ? (
                    <Stat label="Avg rating" value={overview.avgReleaseRating} />
                  ) : null}
                  {data.hasSeller ? (
                    <Stat label="Equipment" value={overview.equipmentCount} />
                  ) : null}
                  {data.isAgency ? (
                    <Stat label="Sites linked" value={overview.buSiteCount} />
                  ) : null}
                </SimpleGrid>
              </Section>

              <Section
                title="What happened"
                hint="Newest first. Tap Open to see the full request."
              >
                {history.length === 0 ? (
                  <Empty label="No activity yet for this member." />
                ) : (
                  <VStack align="stretch" spacing={0}>
                    {history.map((row) => (
                      <SimpleRow
                        key={row.id}
                        what={row.what}
                        where={
                          row.rating != null
                            ? `${row.where} · ★ ${row.rating}`
                            : row.where
                        }
                        when={row.when}
                        status={row.status}
                        onOpen={
                          row.requestId || row.userEquipmentId || row.appUserId
                            ? () => openHistoryRow(row)
                            : undefined
                        }
                      />
                    ))}
                  </VStack>
                )}
              </Section>

              {data.isAgency ? (
                <Section
                  title="People they used"
                  hint="Who this agency sent on jobs (unique)."
                >
                  {(data.agency?.peopleUsed || []).length === 0 ? (
                    <Empty label="No people or equipment used yet." />
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {data.agency.peopleUsed.map((row) => (
                        <SimpleRow
                          key={row.key}
                          what={row.label}
                          where={`Used on ${row.assignCount} job(s)`}
                          when={row.lastAt}
                          onOpen={
                            row.appUserId
                              ? () => openMember(row.appUserId)
                              : row.userEquipmentId
                                ? () => openFleet(row.userEquipmentId)
                                : undefined
                          }
                        />
                      ))}
                    </VStack>
                  )}
                </Section>
              ) : null}

              {data.isAgency ? (
                <Section title="Sites" hint="Business units linked to this agency.">
                  {(data.agency?.connections || []).length === 0 ? (
                    <Empty label="No sites linked." />
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {data.agency.connections.map((row) => (
                        <SimpleRow
                          key={row.id}
                          what={row.buName}
                          where={
                            row.isPrimary
                              ? `${row.siteName || 'All sites'} · Primary`
                              : row.siteName || 'All sites'
                          }
                          when={row.assignedAt}
                        />
                      ))}
                    </VStack>
                  )}
                </Section>
              ) : null}

              {data.hasSeller ? (
                <Section title="Equipment" hint="Fleet linked to this member.">
                  {(data.equipment?.items || []).length === 0 ? (
                    <Empty label="No equipment." />
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {data.equipment.items.map((row) => (
                        <SimpleRow
                          key={row.id}
                          what={row.typeName}
                          where={[
                            row.regNo ? `Reg ${row.regNo}` : row.assetId,
                            row.assignmentCount
                              ? `${row.assignmentCount} job(s)`
                              : null,
                            row.avgRating != null ? `★ ${row.avgRating}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                          onOpen={() => openFleet(row.id)}
                        />
                      ))}
                    </VStack>
                  )}
                </Section>
              ) : null}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
